import { useEffect, useRef } from 'react';
import { fetchPendingFollowups } from '../api';
import type { Followup } from '../types';

export interface InAppNotification {
  id: string;
  type: 'followup' | 'other';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  followupId?: string;
  followupDate?: string;
  followupTime?: string;
  leadId?: string;
  leadName?: string;
  leadPhone?: string;
}

export function useFollowupNotifications(enabled: boolean = true) {
  const notifiedFollowups = useRef<Set<string>>(new Set());
  const inAppNotifications = useRef<InAppNotification[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const listeners = useRef<Set<(notifications: InAppNotification[]) => void>>(new Set());

  useEffect(() => {
    if (!enabled) return;

    // Request notification permission immediately
    if ('Notification' in window) {
      Notification.requestPermission().then((permission) => {
        console.log('Notification permission:', permission);
      });
    }

    const checkFollowups = async () => {
      try {
        const { followups } = await fetchPendingFollowups();
        const now = new Date();
        console.log('Checking follow-ups:', followups.length, 'pending followups');
        
        followups.forEach((fu: any) => {
          // Parse the follow-up date (YYYY-MM-DD) and set time to start of day
          const followupDateTime = new Date(fu.followup_date + 'T00:00:00');
          
          // If time is provided, set it
          if (fu.followup_time) {
            const [hours, minutes] = fu.followup_time.split(':');
            followupDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          } else {
            // If no time, set to end of day so it shows notification on that day
            followupDateTime.setHours(23, 59, 59, 999);
          }
          
          console.log('Follow-up check:', fu.id, 'due:', followupDateTime.toISOString(), 'now:', now.toISOString(), 'isDue:', followupDateTime <= now);
          
          // Check if follow-up date/time has passed and hasn't been notified yet
          if (followupDateTime <= now && !notifiedFollowups.current.has(fu.id)) {
            console.log('Sending notification for follow-up:', fu.id);
            
            // Add in-app notification with lead info
            const leadName = fu.name || 'Unknown';
            const leadPhone = fu.phone || '';
            const notification: InAppNotification = {
              id: fu.id,
              type: 'followup',
              title: 'Follow-up Reminder',
              message: `${leadName} (${leadPhone}) - Follow-up due ${fu.followup_date}${fu.followup_time ? ` at ${fu.followup_time}` : ''}${fu.notes ? `\nNotes: ${fu.notes}` : ''}`,
              timestamp: now,
              read: false,
              followupId: fu.id,
              followupDate: fu.followup_date,
              followupTime: fu.followup_time || undefined,
              leadId: fu.lead_id,
              leadName: leadName,
              leadPhone: leadPhone,
            };
            inAppNotifications.current.unshift(notification);
            notifyListeners();
            
            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`Follow-up: ${leadName}`, {
                body: `${leadPhone} - Due: ${fu.followup_date}${fu.followup_time ? ` at ${fu.followup_time}` : ''}${fu.notes ? `\nNotes: ${fu.notes}` : ''}`,
                icon: '/favicon.ico',
                tag: fu.id,
                requireInteraction: true,
              });
            } else if ('Notification' in window && Notification.permission === 'denied') {
              console.warn('Notification permission denied');
            }
            
            // Mark as notified
            notifiedFollowups.current.add(fu.id);
          }
        });
      } catch (error) {
        console.error('Failed to check follow-ups:', error);
      }
    };

    // Check immediately on mount
    checkFollowups();

    // Check every 1 minute (reduced from 2 for faster response)
    intervalRef.current = setInterval(checkFollowups, 1 * 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled]);

  const notifyListeners = () => {
    listeners.current.forEach(listener => listener([...inAppNotifications.current]));
  };

  // Function to get current notifications
  const getNotifications = () => {
    return [...inAppNotifications.current];
  };

  // Function to mark notification as read
  const markAsRead = (notificationId: string) => {
    const notification = inAppNotifications.current.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      notifyListeners();
    }
  };

  // Function to clear all notifications
  const clearAll = () => {
    inAppNotifications.current = [];
    notifyListeners();
  };

  // Function to subscribe to notification updates
  const subscribe = (listener: (notifications: InAppNotification[]) => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  };

  // Function to clear notified follow-ups (call when follow-up is completed)
  const clearNotification = (followupId: string) => {
    notifiedFollowups.current.delete(followupId);
    // Also remove from in-app notifications
    inAppNotifications.current = inAppNotifications.current.filter(n => n.followupId !== followupId);
    notifyListeners();
  };

  return { 
    clearNotification, 
    getNotifications, 
    markAsRead, 
    clearAll, 
    subscribe 
  };
}
